import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Store, Plus, Pencil } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Field, Input, Select, Textarea } from '@/components/ui/Field';
import { InlineConfirm } from '@/components/ui/InlineConfirm';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { extractLatLngFromMapLink } from '@/lib/googleMapsLink';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../nav';

interface Retailer {
  id: string;
  name: string;
  type: string;
  area: string;
  description: string | null;
  address: string | null;
  hours: string | null;
  phone: string | null;
  map_link: string | null;
  latitude: number | null;
  longitude: number | null;
  sort_order: number;
  status: string;
}

const EMPTY_FORM = {
  name: '',
  type: 'Pharmacy',
  area: 'Others',
  description: '',
  address: '',
  hours: '',
  phone: '',
  map_link: '',
  latitude: '',
  longitude: '',
  sort_order: 0,
  status: 'active',
};

export default function AdminRetailers() {
  const { push } = useToast();
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('rxk_retailers').select('*').order('sort_order', { ascending: true });
    setRetailers((data as Retailer[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId('new');
    setForm(EMPTY_FORM);
  };

  const openEdit = (r: Retailer) => {
    setEditingId(r.id);
    setForm({
      name: r.name,
      type: r.type,
      area: r.area,
      description: r.description ?? '',
      address: r.address ?? '',
      hours: r.hours ?? '',
      phone: r.phone ?? '',
      map_link: r.map_link ?? '',
      latitude: r.latitude != null ? String(r.latitude) : '',
      longitude: r.longitude != null ? String(r.longitude) : '',
      sort_order: r.sort_order,
      status: r.status,
    });
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    setBusy(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const payload = {
        ...form,
        latitude: form.latitude === '' ? null : Number(form.latitude),
        longitude: form.longitude === '' ? null : Number(form.longitude),
        updated_by: user?.id ?? null,
      };
      const { error } =
        editingId === 'new'
          ? await supabase.from('rxk_retailers').insert(payload)
          : await supabase.from('rxk_retailers').update(payload).eq('id', editingId);
      if (error) throw error;
      push('success', editingId === 'new' ? 'Retailer added' : 'Retailer updated');
      setEditingId(null);
      await load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const handleExtractLatLng = async () => {
    const local = extractLatLngFromMapLink(form.map_link);
    if (local) {
      setForm((f) => ({ ...f, latitude: String(local.lat), longitude: String(local.lng) }));
      push('success', 'Coordinates filled in from the map link');
      return;
    }
    // Short share links (maps.app.goo.gl) don't carry coordinates in the
    // URL itself — resolve the redirect server-side instead.
    setExtracting(true);
    try {
      const { data, error } = await supabase.functions.invoke('resolve-map-link', { body: { map_link: form.map_link } });
      if (error || !data?.ok) {
        push('error', data?.details || error?.message || "Couldn't find coordinates in that link");
        return;
      }
      setForm((f) => ({ ...f, latitude: String(data.lat), longitude: String(data.lng) }));
      push('success', 'Coordinates filled in from the map link');
    } finally {
      setExtracting(false);
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('rxk_retailers').delete().eq('id', id);
    if (error) {
      push('error', error.message);
      return;
    }
    push('success', 'Retailer deleted');
    await load();
  };

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-[22px] font-bold text-ink">Where to Buy — Retailers</h1>
          <Button size="sm" onClick={openCreate}>
            <Plus className="w-3.5 h-3.5" /> Add Retailer
          </Button>
        </div>

        <Card>
          <CardHeader title="Locations" subtitle={`${retailers.length} total`} icon={<Store className="w-4 h-4" />} />
          <CardBody className="space-y-1">
            {loading ? (
              <Spinner />
            ) : retailers.length === 0 ? (
              <EmptyState title="No retailers yet" />
            ) : (
              retailers.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 px-2 py-2.5 rounded-lg hover:bg-surface-muted transition-colors duration-150">
                  <button onClick={() => openEdit(r)} className="min-w-0 flex-1 text-left cursor-pointer">
                    <p className="text-[13px] font-semibold text-ink truncate">{r.name}</p>
                    <p className="text-[11.5px] text-ink-faint truncate">{r.type} · {r.area}</p>
                  </button>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge tone={r.status === 'active' ? 'success' : 'neutral'}>{r.status}</Badge>
                    <button onClick={() => openEdit(r)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-faint cursor-pointer">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <InlineConfirm label="Delete" confirmLabel="Confirm" onConfirm={() => handleDelete(r.id)} />
                  </div>
                </div>
              ))
            )}
          </CardBody>
        </Card>
      </div>

      <Modal
        open={!!editingId}
        onClose={() => setEditingId(null)}
        title={editingId === 'new' ? 'Add Retailer' : 'Edit Retailer'}
        icon={<Store className="w-3.5 h-3.5" />}
        busy={busy}
        footer={
          <>
            <Button type="button" variant="secondary" size="md" className="flex-1" onClick={() => setEditingId(null)} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" form="edit-retailer-form" size="md" className="flex-1" loading={busy}>
              Save
            </Button>
          </>
        }
      >
        <form id="edit-retailer-form" onSubmit={handleSave} className="space-y-3.5">
          <Field label="Name">
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <Select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                <option value="Pharmacy">Pharmacy</option>
                <option value="Online">Online</option>
                <option value="Retail">Retail</option>
              </Select>
            </Field>
            <Field label="Area">
              <Select value={form.area} onChange={(e) => setForm((f) => ({ ...f, area: e.target.value }))}>
                {['Gulshan', 'Banani', 'Dhanmondi', 'Uttara', 'Bashundhara', 'Others'].map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <Field label="Short description">
            <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="e.g. Flagship Store" />
          </Field>
          <Field label="Address">
            <Textarea value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} rows={2} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Hours">
              <Input value={form.hours} onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))} placeholder="9:00 AM – 10:00 PM" />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
            </Field>
          </div>
          <Field label="Map link (Get Directions URL)">
            <Input value={form.map_link} onChange={(e) => setForm((f) => ({ ...f, map_link: e.target.value }))} placeholder="https://www.google.com/maps/search/..." />
          </Field>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-semibold text-ink-faint tracking-wide">Map pin (optional)</span>
              <Button type="button" variant="secondary" size="sm" onClick={handleExtractLatLng} disabled={!form.map_link} loading={extracting}>
                Extract from Map Link
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Latitude">
                <Input
                  type="number"
                  step="any"
                  value={form.latitude}
                  onChange={(e) => setForm((f) => ({ ...f, latitude: e.target.value }))}
                  placeholder="23.7808"
                />
              </Field>
              <Field label="Longitude">
                <Input
                  type="number"
                  step="any"
                  value={form.longitude}
                  onChange={(e) => setForm((f) => ({ ...f, longitude: e.target.value }))}
                  placeholder="90.4142"
                />
              </Field>
            </div>
            <p className="text-[10.5px] text-ink-faint mt-1.5">
              Puts a pin on the public Store Locator map. Works automatically for most full Google Maps links (via "Extract from Map Link" above) — for short share links, right-click the spot on Google Maps and tap the coordinates at the top of the menu to copy them in instead.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sort order">
              <Input type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))} />
            </Field>
            <Field label="Status">
              <Select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                <option value="active">Active (visible)</option>
                <option value="inactive">Inactive (hidden)</option>
              </Select>
            </Field>
          </div>
        </form>
      </Modal>
    </AppShell>
  );
}
